/* SignalPills.jsx — calm operational signal pills (P2.1 + P2.2).
 *
 * Two tiny inline components, both restrained on purpose:
 *
 *   <SellerConfidencePill state="Stable" />
 *   <SolicitorResponsivenessPill state="Slowing" />
 *
 * Visual identity:
 *   - 1.5px dot · 11px label · 18px height · soft tinted background.
 *   - No icons by default (an optional sparkline of a chevron only
 *     where the surrounding cell is text-heavy).
 *   - No animations. No gradients. No hover state by default.
 *   - Mobile-safe: never wraps, never shrinks the dot, never overflows.
 *
 * Tone tokens come from `/app/frontend/src/lib/operationalSignals.js`
 * — the source of truth shared with the backend engine.
 */
import React from "react";
import {
  SELLER_CONFIDENCE_TONE,
  SOLICITOR_RESPONSIVENESS_TONE,
} from "../../lib/operationalSignals";

const SIZE = {
  xs: { h: 18, padX: 8, fs: 10.5, dot: 5,  gap: 5 },
  sm: { h: 20, padX: 9, fs: 11,   dot: 5.5, gap: 6 },
  md: { h: 22, padX: 10, fs: 11.5, dot: 6,  gap: 7 },
};

function _basePill({ state, tone, size = "sm", testid, title, withReason, dataState }) {
  if (!tone) return null;
  const s = SIZE[size] || SIZE.sm;

  return (
    <span
      data-testid={testid}
      data-state={dataState || state}
      title={title || state}
      className="inline-flex items-center font-semibold whitespace-nowrap select-none cf-signal-pill"
      style={{
        height: `${s.h}px`,
        padding: `0 ${s.padX}px`,
        gap: `${s.gap}px`,
        background: tone.bg,
        color: tone.fg,
        border: `1px solid ${tone.ring || tone.bg}`,
        borderRadius: 999,
        fontSize: `${s.fs}px`,
        letterSpacing: "0.01em",
        lineHeight: 1,
      }}
    >
      <span
        aria-hidden
        style={{
          width: `${s.dot}px`,
          height: `${s.dot}px`,
          borderRadius: 9999,
          background: tone.dot,
          flex: "0 0 auto",
        }}
      />
      <span style={{ flex: "0 0 auto" }}>{state}</span>
      {withReason ? (
        <span
          className="hidden sm:inline"
          style={{ color: tone.fg, opacity: 0.66, fontWeight: 500, marginLeft: 4 }}
        >
          · {withReason}
        </span>
      ) : null}
    </span>
  );
}

// ─── Seller Confidence Pill ───────────────────────────────────────
export function SellerConfidencePill({
  state,
  size = "sm",
  reason = null,
  testid = "seller-confidence-pill",
}) {
  if (!state) return null;
  const tone = SELLER_CONFIDENCE_TONE[state];
  if (!tone) return null;
  const tooltip = reason ? `${state} · ${reason}` : state;
  return _basePill({
    state,
    tone,
    size,
    testid,
    title: tooltip,
    withReason: reason,
    dataState: state,
  });
}

// ─── Solicitor Responsiveness Pill ────────────────────────────────
export function SolicitorResponsivenessPill({
  state,
  size = "xs",
  reason = null,
  testid = "solicitor-responsiveness-pill",
}) {
  if (!state) return null;
  const tone = SOLICITOR_RESPONSIVENESS_TONE[state];
  if (!tone) return null;
  const tooltip = reason ? `${state} · ${reason}` : state;
  return _basePill({
    state,
    tone,
    size,
    testid,
    title: tooltip,
    withReason: reason,
    dataState: state,
  });
}

export default { SellerConfidencePill, SolicitorResponsivenessPill };
